import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowRight, Check } from 'lucide-react'
import { Button } from '@/components/ui'
import { useSEO } from '@/hooks/useSEO'
import { getUtmContentSlug } from '@/lib/utm-utils'
import { trackCalendlyClick } from '@/lib/analytics'

const CALENDLY_BASE = '/book'

/**
 * Variant B of the landing A/B (started 2026-05-16). Outreach sends
 * Church Music Program + Chamber Ensemble leads here instead of /welcome.
 *
 * Deliberately single-purpose: one CTA (book a call), no secondary links,
 * no case-study grid, no nav. The /welcome control's funnel breaks
 * post-click (healthy opens/clicks, ~0 bookings); this page removes every
 * competing action so the only thing to do is book. Keep it that way:
 * adding links/sections defeats the experiment.
 */
function useCalendlyUrl(): string {
  return useMemo(() => {
    if (typeof window === 'undefined') return CALENDLY_BASE
    const params = new URLSearchParams(window.location.search)
    const forwarded = new URLSearchParams()
    for (const key of ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term']) {
      const value = params.get(key)
      if (value) forwarded.set(key, value)
    }
    const qs = forwarded.toString()
    return qs ? `${CALENDLY_BASE}?${qs}` : CALENDLY_BASE
  }, [])
}

interface CallContent {
  headline: string
  subHeadline: string
}

const DEFAULT_CONTENT: CallContent = {
  headline: 'Fifteen minutes. Leave with a clear plan for your website.',
  subHeadline:
    'I build websites and digital systems for music organizations, and I am also a working musician. Book a short call and I will tell you exactly what I would do for your program, whether or not you hire me.',
}

const CONTENT_BY_SLUG: Record<string, CallContent> = {
  'church-music-program': {
    headline: 'A clear plan for your music program online, in fifteen minutes.',
    subHeadline:
      'I am a working organist and choirmaster who also builds church websites that connect service schedules, choir info, and giving into one place your congregation can find. Book a short call and I will tell you what I would do for your program, whether or not you hire me.',
  },
  'chamber-ensemble': {
    headline: 'A clear plan for your ensemble online, in fifteen minutes.',
    subHeadline:
      'I build fast, modern sites for chamber ensembles that tie your concert calendar, programs, and donor pages together, and I am a classically trained musician myself. Book a short call and I will tell you what I would do, whether or not you hire me.',
  },
}

const TRUST_POINTS = [
  'Built by a working musician and software engineer, not an agency',
  'You leave the call with a concrete plan you can act on yourself',
  'No contract, no pressure. Bring your questions, get straight answers.',
]

export default function CallPage() {
  useSEO({
    title: 'Book a 15-Minute Call | Fugue Lab',
    description:
      'A short call to walk through a clear plan for your music organization’s website. Built by a working musician and software engineer.',
    url: '/call',
  })

  const calendlyUrl = useCalendlyUrl()
  const content = useMemo<CallContent>(() => {
    const slug = getUtmContentSlug()
    return (slug && CONTENT_BY_SLUG[slug]) || DEFAULT_CONTENT
  }, [])

  return (
    <div className="bg-background min-h-screen">
      <header className="border-border/50 border-b px-6 py-4">
        <Link to="/" className="text-lg font-semibold tracking-tight">
          Fugue Lab
        </Link>
      </header>

      <main className="mx-auto max-w-2xl px-6 py-20 sm:py-28">
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="space-y-8 text-center"
        >
          <h1 className="text-foreground text-3xl font-bold tracking-tight md:text-5xl">{content.headline}</h1>
          <p className="text-muted-foreground text-lg leading-relaxed">{content.subHeadline}</p>

          <div className="pt-2">
            <Button size="lg" asChild>
              <a
                href={calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCalendlyClick('call_hero')}
              >
                Book a 15-Minute Call <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>

          <ul className="mx-auto max-w-xl space-y-3 pt-6 text-left">
            {TRUST_POINTS.map((point) => (
              <li key={point} className="text-muted-foreground flex items-start gap-3">
                <Check className="text-primary mt-0.5 h-5 w-5 shrink-0" />
                <span>{point}</span>
              </li>
            ))}
          </ul>

          <div className="pt-8">
            <Button variant="outline" size="lg" asChild>
              <a
                href={calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCalendlyClick('call_footer')}
              >
                Pick a time that works <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </motion.section>
      </main>
    </div>
  )
}
